import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ProgressBar from './ProgressBar';
import BrutalistButton from './BrutalistButton';
import { 
  chapterChecklists, 
  purposeScores, 
  macroChecklistData, 
  assessmentOptions 
} from '../data/checklistData';

export default function Pass1Tab({ state, setState }) {
  // Local state for accordion - stores the ID of the expanded chapter
  const [expandedId, setExpandedId] = useState(null);

  // Robust initialization of chapters if they don't exist in state
  const chapters = state.chapters && state.chapters.length > 0 
    ? state.chapters 
    : [];

  // Initialize first chapter as expanded on mount
  useEffect(() => {
    if (chapters.length > 0 && !expandedId) {
      setExpandedId(chapters[0].id);
    } else if (chapters.length === 0) {
      // If state is completely empty, initialize with 5 default chapters
      const defaultChapters = Array.from({ length: 5 }, (_, i) => ({
        id: `chap-${Date.now()}-${i}`,
        title: `Chapter/Scene ${i + 1}`,
        whatHappens: '',
        checks: {},
        oneSentence: '',
        whatChanges: '',
        purposeScore: ''
      }));
      setState(prev => ({ ...prev, chapters: defaultChapters }));
      setExpandedId(defaultChapters[0].id);
    }
  }, []);

  const macroChecks = state.macroChecks || {};
  const assessment = state.assessment || '';

  // Calculate Progress
  const totalMacroItems = macroChecklistData.reduce((acc, section) => acc + section.items.length, 0);
  const completedMacroItems = Object.values(macroChecks).filter(Boolean).length;
  const assessmentScore = assessment ? 1 : 0;
  const progress = ((completedMacroItems + assessmentScore) / (totalMacroItems + 1)) * 100;

  // Handlers
  const handleAddChapter = () => {
    const newId = `chap-${Date.now()}`;
    const newChapter = {
      id: newId,
      title: `Chapter/Scene ${chapters.length + 1}`,
      whatHappens: '',
      checks: {},
      oneSentence: '',
      whatChanges: '',
      purposeScore: ''
    };
    setState(prev => ({ ...prev, chapters: [...(prev.chapters || []), newChapter] }));
    setExpandedId(newId); // Auto-expand new chapters
  };

  const handleRemoveChapter = (e, idToRemove) => {
    e.stopPropagation(); // Prevent toggling accordion when clicking remove
    if(window.confirm('Remove this chapter summary?')) {
      const filtered = chapters.filter(ch => ch.id !== idToRemove);
      const renumbered = filtered.map((ch, idx) => ({ 
        ...ch, 
        title: `Chapter/Scene ${idx + 1}` 
      }));
      setState(prev => ({ ...prev, chapters: renumbered }));
      
      // If we removed the expanded one, expand the first available
      if (expandedId === idToRemove && renumbered.length > 0) {
        setExpandedId(renumbered[0].id);
      }
    }
  };

  const toggleAccordion = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const updateChapterField = (id, field, value) => {
    setState(prev => ({
      ...prev,
      chapters: prev.chapters.map(ch => ch.id === id ? { ...ch, [field]: value } : ch)
    }));
  };

  const toggleChapterCheck = (chapterId, checkText) => {
    setState(prev => ({
      ...prev,
      chapters: prev.chapters.map(ch => {
        if (ch.id === chapterId) {
          const currentChecks = ch.checks || {};
          return { ...ch, checks: { ...currentChecks, [checkText]: !currentChecks[checkText] } };
        }
        return ch;
      })
    }));
  };

  const toggleMacroCheck = (id) => {
    setState(prev => ({
      ...prev,
      macroChecks: { ...(prev.macroChecks || {}), [id]: !(prev.macroChecks || {})[id] }
    }));
  };

  const handleClear = () => {
    if(window.confirm('Clear all Pass 1 progress?')) {
      const resetChapters = Array.from({ length: 5 }, (_, i) => ({
        id: `chap-reset-${i}`,
        title: `Chapter/Scene ${i + 1}`,
        whatHappens: '',
        checks: {},
        oneSentence: '',
        whatChanges: '',
        purposeScore: ''
      }));
      setState({ chapters: resetChapters, macroChecks: {}, assessment: '' });
      setExpandedId(resetChapters[0].id);
    }
  };

  return (
    <div className="animate-fade-in max-w-4xl">
      <div className="mb-8">
        <h2 className="text-2xl font-bold uppercase tracking-tight mb-2">
          Pass 1 Tools: Macro Structural + Substance Editing
        </h2>
        <p style={{
      fontSize: '16px',
      fontWeight: '500'
    }} className="text-[15px] text-gray-600 leading-relaxed border-l-4 border-orange pl-4 bg-white p-3 shadow-brutal-sm">Guide through Chapter Summaries and Macro Editing structures.</p>
      </div>

      <ProgressBar percentage={progress} passName="Macro Checklist Progress" />

      {/* SECTION 1: CHAPTER SUMMARIES */}
      <div className="mt-12 mb-12">
        <h3 className="font-bold text-xl uppercase tracking-wide mb-6 pb-2 border-b-2 border-black inline-block">
          Chapter Summaries + "Why This Chapter Matters"
        </h3>

        <div className="space-y-4">
          {chapters.map((chapter) => {
            const isExpanded = expandedId === chapter.id;
            return (
              <div key={chapter.id} className="border-2 border-black bg-white shadow-brutal-sm overflow-hidden transition-all">
                {/* Accordion Header */}
                <div 
                  onClick={() => toggleAccordion(chapter.id)}
                  className={`flex justify-between items-center p-4 cursor-pointer transition-colors ${isExpanded ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-50'}`}
                >
                  <div className="flex items-center gap-4">
                    <SafeIcon 
                      icon={isExpanded ? FiIcons.FiChevronDown : FiIcons.FiChevronRight} 
                      className={`text-xl transition-transform ${isExpanded ? 'text-orange' : 'text-gray-400'}`}
                    />
                    <h4 className="font-bold text-sm uppercase tracking-wider">
                      {chapter.title}
                      {chapter.whatHappens && !isExpanded && (
                        <span className="ml-4 lowercase font-normal text-xs text-gray-400 truncate max-w-[200px] hidden sm:inline">
                          — {chapter.whatHappens.substring(0, 40)}...
                        </span>
                      )}
                    </h4>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {chapters.length > 1 && (
                      <button 
                        onClick={(e) => handleRemoveChapter(e, chapter.id)}
                        className={`text-xs uppercase font-bold transition-colors flex items-center gap-1 p-1 hover:bg-red-500 hover:text-white rounded ${isExpanded ? 'text-gray-400' : 'text-gray-300'}`}
                        title="Remove Chapter"
                      >
                        <SafeIcon icon={FiIcons.FiTrash2} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Accordion Content */}
                {isExpanded && (
                  <div className="p-6 animate-slide-down border-t-2 border-black">
                    {/* Field 1 */}
                    <div className="mb-6">
                      <label className="block font-bold mb-2">1. What Happens / What's Taught?</label>
                      <textarea 
                        className="w-full border-2 border-gray-300 focus:border-black p-3 text-sm focus:outline-none transition-colors resize-y min-h-[80px]"
                        value={chapter.whatHappens}
                        onChange={(e) => updateChapterField(chapter.id, 'whatHappens', e.target.value)}
                        placeholder="Summarize the core events or teachings..."
                      />
                    </div>

                    {/* Field 2 */}
                    <div className="mb-6">
                      <label className="block font-bold mb-3">2. Why This Chapter Matters:</label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-offwhite p-4 border border-gray-300">
                        <div>
                          <h5 style={{
      fontSize: '15px'
    }} className="font-bold text-xs text-blue uppercase mb-3 border-b border-gray-300 pb-1">Fiction</h5>
                          <div className="space-y-2">
                            {chapterChecklists.fiction.map(item => (
                              <label key={item} className="flex items-start gap-3 cursor-pointer group">
                                <div className="relative mt-0.5 flex-shrink-0">
                                  <input 
                                    type="checkbox" 
                                    className="peer appearance-none w-4 h-4 border-2 border-black bg-white checked:bg-orange cursor-pointer transition-colors"
                                    checked={chapter.checks?.[item] || false}
                                    onChange={() => toggleChapterCheck(chapter.id, item)}
                                  />
                                  <SafeIcon icon={FiIcons.FiCheck} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-black opacity-0 peer-checked:opacity-100 pointer-events-none stroke-[4]" size={10} />
                                </div>
                                <span style={{
      color: 'rgb(55, 65, 81)',
      fontSize: '15px'
    }} className="text-xs text-gray-700 group-hover:text-black">{item}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h5 style={{
      fontSize: '15px'
    }} className="font-bold text-xs text-orange uppercase mb-3 border-b border-gray-300 pb-1">Nonfiction</h5>
                          <div className="space-y-2">
                            {chapterChecklists.nonfiction.map(item => (
                              <label key={item} className="flex items-start gap-3 cursor-pointer group">
                                <div className="relative mt-0.5 flex-shrink-0">
                                  <input 
                                    type="checkbox" 
                                    className="peer appearance-none w-4 h-4 border-2 border-black bg-white checked:bg-orange cursor-pointer transition-colors"
                                    checked={chapter.checks?.[item] || false}
                                    onChange={() => toggleChapterCheck(chapter.id, item)}
                                  />
                                  <SafeIcon icon={FiIcons.FiCheck} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-black opacity-0 peer-checked:opacity-100 pointer-events-none stroke-[4]" size={10} />
                                </div>
                                <span style={{
      color: 'rgb(55, 65, 81)',
      fontSize: '15px'
    }} className="text-xs text-gray-700 group-hover:text-black">{item}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-bold mb-2">In one sentence, why this matters:</label>
                        <input 
                          type="text"
                          className="w-full border-2 border-gray-300 focus:border-black p-3 text-sm focus:outline-none transition-colors"
                          value={chapter.oneSentence}
                          onChange={(e) => updateChapterField(chapter.id, 'oneSentence', e.target.value)}
                          placeholder="This chapter matters because..."
                        />
                      </div>
                    </div>

                    {/* Field 3 */}
                    <div className="mb-6">
                      <label className="block font-bold mb-2">3. What Changes as a Result?</label>
                      <textarea 
                        className="w-full border-2 border-gray-300 focus:border-black p-3 text-sm focus:outline-none transition-colors resize-y min-h-[80px]"
                        value={chapter.whatChanges}
                        onChange={(e) => updateChapterField(chapter.id, 'whatChanges', e.target.value)}
                        placeholder="Describe the shift..."
                      />
                    </div>

                    {/* Purpose Score */}
                    <div className="bg-orange/5 p-4 border border-orange/20">
                      <label className="block font-bold mb-3 uppercase tracking-wider text-xs">Purpose Score:</label>
                      <div className="flex flex-wrap gap-4">
                        {purposeScores.map(score => (
                          <label key={score} className="flex items-center gap-2 cursor-pointer group">
                            <div className="relative flex-shrink-0">
                              <input 
                                type="radio" 
                                name={`score-${chapter.id}`}
                                className="peer appearance-none w-4 h-4 rounded-full border-2 border-black bg-white checked:bg-black cursor-pointer transition-colors"
                                checked={chapter.purposeScore === score}
                                onChange={() => updateChapterField(chapter.id, 'purposeScore', score)}
                              />
                              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-white opacity-0 peer-checked:opacity-100 pointer-events-none" />
                            </div>
                            <span className="text-xs font-medium text-gray-800 group-hover:text-black">{score}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button 
          onClick={handleAddChapter}
          className="mt-6 flex items-center justify-center gap-2 w-full py-4 border-2 border-dashed border-gray-400 text-gray-500 hover:border-black hover:text-black hover:bg-white transition-all font-bold uppercase tracking-wider text-sm"
        >
          <SafeIcon icon={FiIcons.FiPlus} /> Add Another Chapter/Scene
        </button>
      </div>

      {/* SECTION 2: MACRO EDITING CHECKLIST */}
      <div className="mt-16 mb-12">
        <h3 className="font-bold text-xl uppercase tracking-wide mb-6 pb-2 border-b-2 border-black inline-block">
          Macro Editing Checklist
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
          {macroChecklistData.map((category) => (
            <div key={category.id}>
              <h4 className="font-bold text-black uppercase tracking-tight mb-4 border-l-4 border-blue pl-3">
                {category.title}
              </h4>
              <div className="space-y-3">
                {category.items.map(item => {
                  const isChecked = macroChecks[item.id] || false;
                  return (
                    <label key={item.id} className="group flex items-start gap-3 cursor-pointer">
                      <div className="relative mt-0.5 flex-shrink-0">
                        <input 
                          type="checkbox" 
                          className="peer appearance-none w-5 h-5 border-2 border-gray-400 bg-white checked:border-black checked:bg-blue cursor-pointer transition-colors group-hover:border-black"
                          checked={isChecked}
                          onChange={() => toggleMacroCheck(item.id)}
                        />
                        <SafeIcon icon={FiIcons.FiCheck} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 pointer-events-none stroke-[3]" size={14} />
                      </div>
                      <span style={{
      fontSize: '15px'
    }} className={`text-[14px] leading-snug transition-colors duration-200
                        ${isChecked ? 'text-gray-500 line-through' : 'text-gray-800 group-hover:text-black'}`}>
                        {item.text}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FOOTER ACTIONS */}
      <div className="mt-12 flex justify-start border-t-2 border-black pt-8">
        <BrutalistButton onClick={handleClear} variant="secondary">
          Clear All Pass 1 Progress
        </BrutalistButton>
      </div>
    </div>
  );
}